// Teams Backup Viewer — reads an archive folder produced by the (retired)
// backup tool and renders it in-browser. No auth, no network calls to Graph,
// no writes back to the folder except for the auto-rebuilt manifest when a
// partial archive is opened.

import { slugify, safeFilename, shortId } from '../src/util/slug.mjs';

// ---------- feature detection ----------
if (typeof window.showDirectoryPicker !== 'function') {
  document.querySelectorAll('.step').forEach((s) => (s.hidden = true));
  document.querySelector('[data-step="unsupported"]').hidden = false;
  throw new Error('unsupported');
}

// ---------- helpers ----------
const $ = (id) => document.getElementById(id);
const $$ = (sel) => document.querySelectorAll(sel);
const KINDS = [
  { key: 'chats', label: 'Chats' },
  { key: 'meeting-chats', label: 'Meetings' },
  { key: 'channels', label: 'Channels' },
];
const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' })[c]);
const initials = (n) => (String(n || '').trim().split(/\s+/).slice(0, 2).map((p) => p[0] || '').join('') || '?').toUpperCase();

function setStep(name) {
  $$('.step').forEach((s) => (s.hidden = s.dataset.step !== name));
}

// ---------- IndexedDB (folder handle + favorites) ----------
const DB_NAME = 'teams-backup-viewer';
const DB_STORE = 'store';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => { req.result.createObjectStore(DB_STORE); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbGet(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readonly');
    const req = tx.objectStore(DB_STORE).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}
async function idbPut(key, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function saveOpenedHandle(handle, displayName) {
  await idbPut('last-handle', { handle, displayName, savedAt: new Date().toISOString() }).catch(() => null);
}
async function loadLastHandle() {
  return await idbGet('last-handle').catch(() => null);
}
async function loadFavorites() {
  const arr = await idbGet('favorites').catch(() => null);
  return new Set(Array.isArray(arr) ? arr : []);
}
async function saveFavorites(set) {
  await idbPut('favorites', [...set]).catch(() => null);
}

async function verifyHandlePermission(handle, { prompt = false } = {}) {
  if (!handle) return false;
  try {
    const opts = { mode: 'readwrite' };
    if ((await handle.queryPermission(opts)) === 'granted') return true;
    if (!prompt) return false;
    return (await handle.requestPermission(opts)) === 'granted';
  } catch { return false; }
}

// ---------- folder open + validate ----------
// Walk into `dir` looking for a subdir with data/manifest.json OR at least a
// data/<something>/ dir (partial archive). 2 levels deep so a user can pick
// the parent of the archive folder if they want.
async function findArchiveDir(dir, remaining, pathParts) {
  try {
    const dataDir = await dir.getDirectoryHandle('data');
    try {
      await dataDir.getFileHandle('manifest.json');
      return { dir, pathParts, hasManifest: true };
    } catch { /* no manifest — check for any conversation folder */ }
    for await (const child of dataDir.values()) {
      if (child.kind === 'directory') return { dir, pathParts, hasManifest: false };
    }
  } catch { /* no data dir here — recurse */ }
  if (remaining <= 0) return null;
  try {
    for await (const child of dir.values()) {
      if (child.kind !== 'directory') continue;
      const found = await findArchiveDir(child, remaining - 1, [...pathParts, child.name]);
      if (found) return found;
    }
  } catch { /* no permission to iterate */ }
  return null;
}

async function pickArchiveFolder() {
  try {
    const parent = await window.showDirectoryPicker({ mode: 'readwrite', id: 'teams-backup-viewer', startIn: 'documents' });
    const found = await findArchiveDir(parent, 2, []);
    if (!found) {
      alert("That folder doesn't look like a Teams Backup archive — I searched 2 levels deep and couldn't find a data/ folder inside it.");
      return;
    }
    const displayName = found.pathParts.length ? `${parent.name}/${found.pathParts.join('/')}` : parent.name;
    await saveOpenedHandle(found.dir, displayName);
    await enterBrowse(found.dir, displayName);
  } catch (err) {
    if (err.name !== 'AbortError') alert(`Couldn't open folder: ${err.message}`);
  }
}

$('btn-open').addEventListener('click', pickArchiveFolder);
$('btn-open-other').addEventListener('click', pickArchiveFolder);

// ---------- rebuild manifest from disk (for partial archives) ----------
async function rebuildManifestFromDisk(rootHandle) {
  const entries = [];
  const dataDir = await rootHandle.getDirectoryHandle('data').catch(() => null);
  if (!dataDir) return { generatedAt: null, conversations: [] };

  async function readEntry(dir, kind, relDir) {
    try {
      const fh = await dir.getFileHandle('messages.json');
      const file = await fh.getFile();
      const payload = JSON.parse(await file.text());
      const msgs = payload.messages || [];
      return {
        id: payload.id,
        kind,
        name: payload.name || dir.name,
        slug: dir.name,
        relDir,
        conversationJs: `${relDir}/conversation.js`,
        messagesJson: `${relDir}/messages.json`,
        messageCount: msgs.length,
        attachmentCount: msgs.reduce((n, m) => n + (m.attachments?.filter((a) => a.localPath).length || 0), 0),
        recordingCount: msgs.reduce((n, m) => n + (m.event?.kind === 'recording' && m.event?.localPath ? 1 : 0), 0),
        transcriptCount: msgs.reduce((n, m) => n + (m.event?.transcriptLocalPath ? 1 : 0), 0),
        lastMessageAt: msgs.length ? (msgs[msgs.length - 1].timestamp || null) : null,
      };
    } catch { return null; }
  }

  for (const kind of ['chats', 'meeting-chats']) {
    const kd = await dataDir.getDirectoryHandle(kind).catch(() => null);
    if (!kd) continue;
    for await (const conv of kd.values()) {
      if (conv.kind !== 'directory') continue;
      const e = await readEntry(conv, kind, `data/${kind}/${conv.name}`);
      if (e) entries.push(e);
    }
  }
  const chDir = await dataDir.getDirectoryHandle('channels').catch(() => null);
  if (chDir) {
    for await (const team of chDir.values()) {
      if (team.kind !== 'directory') continue;
      for await (const chan of team.values()) {
        if (chan.kind !== 'directory') continue;
        const e = await readEntry(chan, 'channels', `data/channels/${team.name}/${chan.name}`);
        if (e) entries.push(e);
      }
    }
  }
  entries.sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''));
  return { generatedAt: null, conversations: entries };
}

async function persistRebuiltManifest(rootHandle, manifest) {
  try {
    const dataDir = await rootHandle.getDirectoryHandle('data', { create: true });
    const fh = await dataDir.getFileHandle('manifest.json', { create: true });
    const w = await fh.createWritable();
    await w.write(JSON.stringify(manifest, null, 2));
    await w.close();
  } catch { /* read-only mount, skip persistence */ }
}

async function loadOrRebuildManifest(rootHandle) {
  try {
    const dataDir = await rootHandle.getDirectoryHandle('data');
    const fh = await dataDir.getFileHandle('manifest.json');
    const file = await fh.getFile();
    return JSON.parse(await file.text());
  } catch {
    const rebuilt = await rebuildManifestFromDisk(rootHandle);
    if (rebuilt.conversations.length) await persistRebuiltManifest(rootHandle, rebuilt);
    return rebuilt;
  }
}

// ---------- browse view ----------
const browseState = {
  rootHandle: null,
  displayName: '',
  manifest: null,
  active: null,
  search: '',
  favorites: new Set(),
  filterMode: 'all', // 'all' | 'starred'
};

async function enterBrowse(rootHandle, displayName) {
  setStep('browse');
  browseState.rootHandle = rootHandle;
  browseState.displayName = displayName;
  browseState.favorites = await loadFavorites();
  $('browse-subtitle').textContent = displayName || '';
  $('opened-info').textContent = displayName || '';
  $('browse-welcome').hidden = false;
  $('browse-conv').hidden = true;
  $('browse-conv-list').innerHTML = `<div class="loading"><div class="spinner"></div><span>Loading manifest…</span></div>`;
  try {
    browseState.manifest = await loadOrRebuildManifest(rootHandle);
    $('partial-banner').hidden = !(browseState.manifest.generatedAt === null && (browseState.manifest.conversations?.length || 0) > 0);
    renderBrowseSidebar();
  } catch (err) {
    $('browse-conv-list').innerHTML = `<div class="loading" style="color:var(--danger)">Couldn't open archive: ${escapeHtml(err.message)}</div>`;
  }
}

$('browse-search').addEventListener('input', (e) => { browseState.search = e.target.value; renderBrowseSidebar(); });
$$('.filter-tab').forEach((tab) => {
  tab.addEventListener('click', () => {
    browseState.filterMode = tab.dataset.filter;
    $$('.filter-tab').forEach((t) => t.classList.toggle('active', t === tab));
    renderBrowseSidebar();
  });
});

function renderBrowseSidebar() {
  const list = $('browse-conv-list');
  list.innerHTML = '';
  const q = browseState.search.trim().toLowerCase();
  const convs = (browseState.manifest?.conversations || [])
    .filter((c) => !q || (c.name || '').toLowerCase().includes(q))
    .filter((c) => browseState.filterMode !== 'starred' || browseState.favorites.has(c.id))
    .sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''));
  let any = false;
  for (const { key, label } of KINDS) {
    const items = convs.filter((c) => c.kind === key);
    if (!items.length) continue;
    any = true;
    const head = document.createElement('div');
    head.className = 'section-head';
    head.innerHTML = `<span>${label}</span><span class="count">${items.length}</span>`;
    list.appendChild(head);
    for (const conv of items) {
      const row = document.createElement('div');
      row.className = 'browse-conv-row' + (browseState.active?.id === conv.id ? ' active' : '');
      const starred = browseState.favorites.has(conv.id);
      row.innerHTML = `
        <button class="star-btn" title="${starred ? 'Unstar' : 'Star'} this chat">${starred ? '★' : '☆'}</button>
        <div class="avatar"></div>
        <div class="info"><div class="name"></div><div class="meta"></div></div>`;
      row.querySelector('.avatar').textContent = initials(conv.name);
      row.querySelector('.name').textContent = conv.name || '(unnamed)';
      const bits = [];
      if (conv.messageCount != null) bits.push(`${conv.messageCount} msgs`);
      if (conv.attachmentCount) bits.push(`📎 ${conv.attachmentCount}`);
      if (conv.recordingCount) bits.push(`🎥 ${conv.recordingCount}`);
      row.querySelector('.meta').textContent = bits.join(' · ');
      const starBtn = row.querySelector('.star-btn');
      if (starred) starBtn.classList.add('active');
      starBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (browseState.favorites.has(conv.id)) browseState.favorites.delete(conv.id);
        else browseState.favorites.add(conv.id);
        await saveFavorites(browseState.favorites);
        renderBrowseSidebar();
      });
      row.addEventListener('click', () => openBrowseConv(conv));
      list.appendChild(row);
    }
  }
  if (!any) {
    list.innerHTML = browseState.filterMode === 'starred'
      ? `<div class="loading">No starred conversations yet. Click ☆ next to any chat to star it.</div>`
      : `<div class="loading">No conversations match.</div>`;
  }
}

async function openBrowseConv(conv) {
  browseState.active = conv;
  renderBrowseSidebar();
  $('browse-welcome').hidden = true;
  $('browse-conv').hidden = false;
  $('browse-conv-title').textContent = conv.name || '(unnamed)';
  $('browse-conv-sub').textContent = 'Loading…';
  $('browse-messages').innerHTML = `<div class="loading"><div class="spinner"></div><span>Loading conversation…</span></div>`;
  $('msg-search').value = '';
  try {
    const parts = conv.relDir.split('/').filter(Boolean);
    let dir = browseState.rootHandle;
    for (const p of parts) dir = await dir.getDirectoryHandle(p);
    const file = await (await dir.getFileHandle('messages.json')).getFile();
    const payload = JSON.parse(await file.text());
    await renderBrowseConv(conv, payload, dir);
  } catch (err) {
    $('browse-messages').innerHTML = `<div class="loading" style="color:var(--danger)">Failed: ${escapeHtml(err.message)}</div>`;
  }
}

async function renderBrowseConv(conv, payload, convDir) {
  const members = (payload.members || []).map((m) => m.displayName).filter(Boolean);
  const bits = [];
  if (payload.chatType) bits.push(payload.chatType);
  if (members.length) bits.push(members.join(', '));
  bits.push(`${payload.messageCount} messages`);
  $('browse-conv-sub').textContent = bits.join(' · ');

  let attachmentsDir = null;
  try { attachmentsDir = await convDir.getDirectoryHandle('attachments'); } catch { /* none */ }
  const attachmentBlobUrls = new Map();

  async function getAttachmentUrl(relPath) {
    const filename = relPath.split('/').pop();
    if (attachmentBlobUrls.has(filename)) return attachmentBlobUrls.get(filename);
    if (!attachmentsDir) return null;
    try {
      const fh = await attachmentsDir.getFileHandle(filename);
      const file = await fh.getFile();
      const url = URL.createObjectURL(file);
      attachmentBlobUrls.set(filename, url);
      return url;
    } catch { return null; }
  }

  // Preload inline images so they render immediately.
  const inlineNeeded = new Set();
  for (const msg of payload.messages || []) {
    const html = msg.body?.content || '';
    for (const m of html.matchAll(/(src|href)="attachments\/([^"]+)"/gi)) inlineNeeded.add(m[2]);
  }
  await Promise.all([...inlineNeeded].map((n) => getAttachmentUrl(`attachments/${n}`)));

  const container = $('browse-messages');
  container.innerHTML = '';
  let currentDay = null;
  let lastAuthor = null;
  let lastGroup = null;

  for (const msg of payload.messages || []) {
    const d = msg.timestamp ? new Date(msg.timestamp) : null;
    if (d) {
      const key = d.toDateString();
      if (key !== currentDay) {
        const sep = document.createElement('div');
        sep.className = 'day-sep';
        sep.textContent = d.toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        container.appendChild(sep);
        currentDay = key;
        lastAuthor = null;
      }
    }
    const isSystem = msg.messageType === 'systemEventMessage' || msg.author?.kind === 'system';
    const authorKey = msg.author?.id || msg.author?.name;
    const sameAuthor = !isSystem && lastAuthor === authorKey;

    if (!sameAuthor) {
      lastGroup = document.createElement('div');
      lastGroup.className = 'msg-group' + (isSystem ? ' system' : '');
      const av = document.createElement('div'); av.className = 'avatar'; av.textContent = initials(msg.author?.name || '?');
      const body = document.createElement('div'); body.className = 'msg-body';
      const head = document.createElement('div'); head.className = 'msg-head';
      const auth = document.createElement('span'); auth.className = 'msg-author'; auth.textContent = msg.author?.name || 'Unknown';
      const time = document.createElement('span'); time.className = 'msg-time'; time.textContent = d ? d.toLocaleString() : '';
      head.append(auth, time);
      body.appendChild(head);
      lastGroup.append(av, body);
      container.appendChild(lastGroup);
      lastAuthor = authorKey;
    }
    const target = lastGroup.querySelector('.msg-body');
    target.appendChild(await renderBrowseMessage(msg, attachmentBlobUrls, getAttachmentUrl));
    lastGroup.dataset.search = (lastGroup.dataset.search || '') + ' ' + msgText(msg);
  }

  requestAnimationFrame(() => {
    container.scrollTop = container.scrollHeight;
    updateJumpButtons();
  });
}

async function renderBrowseMessage(msg, attachmentBlobUrls, getAttachmentUrl) {
  if (msg.event?.kind === 'recording') return await renderRecording(msg, getAttachmentUrl);
  if (msg.messageType === 'systemEventMessage' || msg.event) {
    const b = document.createElement('div'); b.className = 'msg system';
    b.textContent = msg.event ? `[${msg.event.kind}]` : (msg.body?.content ? stripTags(msg.body.content) : '[system event]');
    return b;
  }
  const wrap = document.createElement('div');
  const bubble = document.createElement('div'); bubble.className = 'msg';
  if (msg.body?.contentType === 'html') {
    bubble.innerHTML = sanitizeHtml(rewriteInlinePaths(msg.body.content || '', attachmentBlobUrls));
  } else {
    bubble.textContent = msg.body?.content || '';
  }
  if (msg.lastEditedDateTime) {
    const e = document.createElement('span'); e.className = 'edited'; e.textContent = '(edited)';
    bubble.appendChild(e);
  }
  wrap.appendChild(bubble);

  const files = (msg.attachments || []).filter((a) => a.name || a.localPath);
  if (files.length) {
    const box = document.createElement('div'); box.className = 'attachment-list';
    for (const att of files) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'attachment' + (att.localPath ? '' : ' missing');
      btn.innerHTML = `<span class="att-icon"></span><div class="att-info"><div class="att-name"></div><div class="att-type"></div></div>`;
      btn.querySelector('.att-icon').textContent = fileIcon(att.name || '');
      btn.querySelector('.att-name').textContent = att.name || '(unnamed)';
      btn.querySelector('.att-type').textContent = att.contentType || '';
      if (att.localPath) {
        btn.addEventListener('click', async () => {
          const url = await getAttachmentUrl(att.localPath);
          if (url) window.open(url, '_blank');
        });
      } else {
        btn.disabled = true;
      }
      box.appendChild(btn);
    }
    wrap.appendChild(box);
  }
  if (msg.reactions?.length) {
    const rx = document.createElement('div'); rx.className = 'reactions';
    for (const r of msg.reactions) {
      const chip = document.createElement('span'); chip.className = 'reaction';
      chip.textContent = `${emojiFor(r.reactionType)} ${r.user || ''}`.trim();
      rx.appendChild(chip);
    }
    wrap.appendChild(rx);
  }
  return wrap;
}

async function renderRecording(msg, getAttachmentUrl) {
  const w = document.createElement('div'); w.className = 'recording';
  const t = document.createElement('div'); t.className = 'rec-title';
  t.textContent = '🎥 ' + (msg.event.displayName || 'Meeting recording');
  w.appendChild(t);
  if (msg.event.duration) {
    const m = document.createElement('div'); m.className = 'rec-meta'; m.textContent = `duration ${msg.event.duration}`;
    w.appendChild(m);
  }
  if (msg.event.localPath) {
    const url = await getAttachmentUrl(msg.event.localPath);
    if (url) {
      const v = document.createElement('video');
      v.controls = true; v.preload = 'metadata'; v.src = url;
      w.appendChild(v);
    }
  } else {
    const p = document.createElement('div'); p.className = 'rec-meta';
    p.textContent = 'Recording not available in this archive.';
    w.appendChild(p);
  }
  if (msg.event.transcriptLocalPath) {
    const a = document.createElement('a'); a.className = 'rec-transcript';
    a.href = '#'; a.textContent = '📝 Open transcript (VTT)';
    a.addEventListener('click', async (e) => {
      e.preventDefault();
      const url = await getAttachmentUrl(msg.event.transcriptLocalPath);
      if (url) window.open(url, '_blank');
    });
    w.appendChild(a);
  }
  return w;
}

function rewriteInlinePaths(html, attachmentBlobUrls) {
  return html.replace(/(src|href)="attachments\/([^"]+)"/gi, (_m, attr, name) => {
    const url = attachmentBlobUrls.get(name);
    return url ? `${attr}="${url}"` : `${attr}=""`;
  });
}

function sanitizeHtml(html) {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  (function walk(node) {
    for (const el of [...node.children]) {
      const tag = el.tagName.toLowerCase();
      if (['script', 'style', 'iframe', 'object'].includes(tag)) { el.remove(); continue; }
      for (const attr of [...el.attributes]) {
        const n = attr.name.toLowerCase();
        if (n.startsWith('on')) el.removeAttribute(attr.name);
        if ((n === 'src' || n === 'href') && /^\s*(javascript|data):/i.test(attr.value)) el.removeAttribute(attr.name);
        if (n === 'src' && /^https?:/i.test(attr.value)) {
          el.removeAttribute('src');
          el.setAttribute('alt', '[remote image removed]');
        }
      }
      walk(el);
    }
  })(root);
  return root.innerHTML;
}
function stripTags(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}
function msgText(msg) {
  if (!msg.body?.content) return '';
  if (msg.body.contentType === 'text') return msg.body.content;
  return stripTags(msg.body.content);
}
function fileIcon(name) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['pdf'].includes(ext)) return '📕';
  if (['doc','docx'].includes(ext)) return '📘';
  if (['xls','xlsx','csv'].includes(ext)) return '📗';
  if (['ppt','pptx'].includes(ext)) return '📙';
  if (['png','jpg','jpeg','gif','webp','svg'].includes(ext)) return '🖼️';
  if (['mp4','mov','webm','mkv'].includes(ext)) return '🎬';
  if (['mp3','wav','m4a','ogg'].includes(ext)) return '🎵';
  if (['zip','tar','gz','7z','rar'].includes(ext)) return '🗜️';
  if (['vtt','srt'].includes(ext)) return '📝';
  return '📎';
}
function emojiFor(t) { return { like:'👍', heart:'❤️', laugh:'😄', surprised:'😮', sad:'😢', angry:'😠' }[t] || '👍'; }

// ---------- message search + jump buttons ----------
$('msg-search').addEventListener('input', (e) => {
  const q = e.target.value.toLowerCase().trim();
  for (const g of document.querySelectorAll('#browse-messages .msg-group')) {
    const t = (g.dataset.search || '').toLowerCase();
    g.classList.toggle('hidden', !!q && !t.includes(q));
  }
});
function updateJumpButtons() {
  const c = $('browse-messages');
  if (!c) return;
  const nearBottom = c.scrollHeight - c.scrollTop - c.clientHeight < 100;
  $('btn-jump-top').hidden = false;
  $('btn-jump-bottom').hidden = nearBottom;
}
$('btn-jump-top').addEventListener('click', () => {
  const c = $('browse-messages');
  c.scrollTo({ top: 0, behavior: 'smooth' });
});
$('btn-jump-bottom').addEventListener('click', () => {
  const c = $('browse-messages');
  c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' });
});
$('browse-messages').addEventListener('scroll', updateJumpButtons, { passive: true });

// ---------- init ----------
(async () => {
  setStep('open');
  // If they've opened an archive before AND we can still access it, offer to
  // reopen without a re-pick (still one click, still requires user gesture).
  const last = await loadLastHandle();
  if (last?.handle) {
    const ok = await verifyHandlePermission(last.handle, { prompt: false });
    if (ok || true) {
      // Show the hint regardless — the button click will re-prompt if needed.
      $('last-hint').hidden = false;
      $('last-name').textContent = last.displayName || last.handle.name;
      $('btn-open-last').addEventListener('click', async (e) => {
        e.preventDefault();
        const okNow = await verifyHandlePermission(last.handle, { prompt: true });
        if (!okNow) {
          alert("Couldn't re-open that folder — it may have been moved or permission was denied. Pick it again with the button above.");
          $('last-hint').hidden = true;
          return;
        }
        await enterBrowse(last.handle, last.displayName || last.handle.name);
      });
    }
  }
})();
